import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { toast, useToast } from "@shared/hooks/use-toast";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { axAssetRegistry, axAssets } from "./api";
import {
  AssetFilters,
  ComplianceHistoryFilters,
  FacilityFilters,
  NetworkFilters,
  ServiceOrderFilters,
  SystemFilters,
} from "./types";

export const useFacilitiesList = (filters?: Record<string, any>) => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.ax.facilities.list(filters),
    () => axAssetRegistry.getFacilities(filters),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const useFacilityDetails = (facilityId: string) => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.ax.facilities.details(facilityId, remoteUtilityId),
    () => axAssetRegistry.getFacilityDetails(facilityId, remoteUtilityId),
    {
      enabled: !!remoteUtilityId && !!facilityId,
    }
  );
};

export const useFacilityPerformance = (facilityId: number) => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.ax.facilities.performance(
      facilityId,
      remoteUtilityId
    ),
    () => axAssetRegistry.getFacilityPerformance(facilityId, remoteUtilityId),
    {
      enabled: !!remoteUtilityId && !!facilityId,
    }
  );
};

export const useCreateFacility = () => {
  const { toast } = useToast();
  const utilityId = useRemoteUtilityId();

  return useSmartMutation(
    (formData: FormData): Promise<any> => {
      formData.append("remote_utility_id", utilityId);
      return axAssetRegistry.createFacility(formData);
    },
    {
      onSuccess: () => {
        toast({
          title: "Success!",
          description: "Facility created successfully",
        });

        queryClient.refetchQueries({
          queryKey: ["ax", "facilities", "list"],
        });
      },

      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to create facility",
          variant: "destructive",
        });
      },
    }
  );
};

export const useUpdateFacility = () => {
  const { toast } = useToast();
  const utilityId = useRemoteUtilityId();

  return useSmartMutation(
    ({
      facilityId,
      formData,
    }: {
      facilityId: string;
      formData: FormData;
    }): Promise<any> => {
      formData.append("remote_utility_id", utilityId);
      return axAssetRegistry.updateFacility(facilityId, formData);
    },
    {
      onSuccess: (data, { facilityId }) => {
        toast({
          title: "Success!",
          description: "Facility updated successfully",
        });

        queryClient.invalidateQueries({
          queryKey: QueryKeyFactory.module.ax.facilities.details(
            facilityId,
            utilityId
          ),
        });
        queryClient.removeQueries({
          queryKey: QueryKeyFactory.module.ax.facilities.dashboard(utilityId),
        });

        queryClient.refetchQueries({
          queryKey: ["ax", "facilities", "list"],
        });
      },

      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update facility",
          variant: "destructive",
        });
      },
    }
  );
};

export const useFacilityStatuses = () => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.facilities.choices.statuses(),
    () => axAssetRegistry.getFacilityStatuses()
  );
};

export const useOperationalHours = () => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.facilities.choices.operationalHours(),
    () => axAssetRegistry.getOperationalHours()
  );
};

export const useDepreciationSchedule = () => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.facilities.choices.depreciationSchedule(),
    () => axAssetRegistry.getDepreciationSchedule()
  );
};

export const useFacilityTypes = () => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.ax.facilities.choices.types(remoteUtilityId),
    () => axAssetRegistry.getFacilityTypes(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const useFacilityUtilityServices = () => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.ax.facilities.choices.utilityServices(
      remoteUtilityId
    ),
    () => axAssetRegistry.getFacilityUtilityServices(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const useFacilityAreas = () => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.ax.facilities.choices.areas(remoteUtilityId),
    () => axAssetRegistry.getFacilityAreas(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const useFacilityZones = () => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.ax.facilities.choices.zones(remoteUtilityId),
    () => axAssetRegistry.getFacilityZones(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const useFacilityManagers = () => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.ax.facilities.choices.managers(remoteUtilityId),
    () => axAssetRegistry.getFacilityManagers(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
    }
  );
};
export const useFacilitiesSummary = () => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.ax.facilities.dashboard(remoteUtilityId),
    () => axAssetRegistry.getFacilitiesSummary(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const useFacilityFinance = (facilityId: number) => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.ax.facilities.finance.finance(
      facilityId,
      remoteUtilityId
    ),
    () => axAssetRegistry.getFacilityFinance(facilityId, remoteUtilityId),
    {
      enabled: !!remoteUtilityId && !!facilityId,
    }
  );
};

export const useFacilitySystem = (facilityId: number) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.facilities.system.system(facilityId),
    () => axAssetRegistry.getFacilitySystem(facilityId),
    {
      enabled: !!facilityId,
    }
  );
};

export const useDeleteFacility = () => {
  const { toast } = useToast();
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartMutation(
    ({ facilityId }: { facilityId: string }): Promise<any> => {
      return axAssetRegistry.deleteFacility(facilityId, remoteUtilityId);
    },
    {
      onSuccess: (data, { facilityId }) => {
        toast({
          title: "Success!",
          description: "Facility deleted successfully",
        });

        queryClient.invalidateQueries({
          queryKey: ["ax", "facilities", "list"],
        });

        queryClient.removeQueries({
          queryKey: QueryKeyFactory.module.ax.facilities.details(
            facilityId,
            remoteUtilityId
          ),
        });

        queryClient.invalidateQueries({
          queryKey:
            QueryKeyFactory.module.ax.facilities.dashboard(remoteUtilityId),
        });

        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.ax.facilities
            .dashboard(remoteUtilityId)
            .slice(0, 4),
        });
      },

      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to delete facility",
          variant: "destructive",
        });
      },
    }
  );
};

export const useNetworksList = (filters: NetworkFilters = {}) => {
  const remoteUtilityId = useRemoteUtilityId();

  const stableFilters = useMemo(() => {
    const sortedFilters = { ...filters };

    if (sortedFilters.networkType) {
      sortedFilters.networkType = [...sortedFilters.networkType].sort();
    }
    if (sortedFilters.operationalStatus) {
      sortedFilters.operationalStatus = [
        ...sortedFilters.operationalStatus,
      ].sort();
    }

    return sortedFilters;
  }, [filters]);

  return useSmartQuery(
    QueryKeyFactory.module.ax.networks.networkList(stableFilters),
    () => axAssetRegistry.getNetworks(remoteUtilityId, stableFilters),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const useServiceOrdersList = (filters: ServiceOrderFilters = {}) => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.ax.facilities.serviceOrders.list(
      remoteUtilityId,
      filters
    ),
    () => axAssetRegistry.getServiceOrders(remoteUtilityId, filters),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

const asset = {
  useAssetsList: (filters: AssetFilters = {}) => {
    const remoteUtilityId = useRemoteUtilityId();

    const search = filters.searchData || "";
    const systemNetwork = filters.systemNetwork;
    const location = "";
    const conditionScore = filters.conditionScore;
    const page = filters.page;

    const facilityId = filters.facilityId || 0;

    return useSmartQuery(
      QueryKeyFactory.module.ax.assets.assetList({
        remoteUtilityId,
        search,
        systemNetwork,
        location,
        conditionScore,
        facilityId,
        page,
      }),
      () => axAssets.getAssets(remoteUtilityId, filters),
      {
        enabled: !!remoteUtilityId,
      }
    );
  },
};

export { asset };

export const useSystemsList = (filters: SystemFilters = {}) => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.ax.systemManagement.getAllSystems(
      Number(remoteUtilityId),
      1,
      filters
    ),
    () => axAssetRegistry.getSystems(remoteUtilityId, filters),
    {
      enabled: !!remoteUtilityId,
    }
  );
};
export const useFacilityAlerts = (facilityId: string) => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.ax.facilities.alerts(facilityId, remoteUtilityId),
    () => axAssetRegistry.getFacilityAlerts(facilityId, remoteUtilityId),
    {
      enabled: !!remoteUtilityId && !!facilityId,
    }
  );
};
export const useFacilityCompliance = (facilityId: string) => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.ax.facilities.compliance(
      facilityId,
      remoteUtilityId
    ),
    () => axAssetRegistry.getFacilityCompliance(facilityId, remoteUtilityId),
    {
      enabled: !!remoteUtilityId && !!facilityId,
    }
  );
};
export const useFacilityComplianceHistory = (
  facilityId: string,
  filters: ComplianceHistoryFilters = {}
) => {
  const remoteUtilityId = useRemoteUtilityId();

  const stableFilters = useMemo(() => {
    return { ...filters };
  }, [filters]);

  return useSmartQuery(
    QueryKeyFactory.module.ax.facilities.complianceHistory(
      facilityId,
      remoteUtilityId,
      stableFilters
    ),
    () =>
      axAssetRegistry.getFacilityComplianceHistory(
        facilityId,
        remoteUtilityId,
        stableFilters
      ),
    {
      enabled: !!remoteUtilityId && !!facilityId,
    }
  );
};
export const useToggleFacilitiesStatus = (filters: FacilityFilters = {}) => {
  const queryClient = useQueryClient();
  return useSmartMutation(
    async (data: { listId: number; isActive: boolean }) => {
      if (!data.listId) {
        throw new Error("Template ID is missing");
      }

      return axAssetRegistry.toggleFacilityStatus(data.listId, data.isActive);
    },
    {
      onSuccess: (data, variables) => {
        toast({
          title: "Success",
          description: `Template ${
            !data.isActive ? "activated" : "deactivated"
          } successfully`,
        });
        queryClient.invalidateQueries({
          queryKey:
            QueryKeyFactory.module.ax.facilities.facilityFilter(filters),
        });
      },
      onError: (error: any) => {
        toast({
          variant: "destructive",
          title: "",
          description: error?.message || "Failed to save template as draft",
        });
      },
    }
  );
};

export const useGetSystemConditionAndRiskById = (
  assessmentName: string,
  id: number
) => {
  const remoteUtilityId = useRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ax.facilities.getSystemConditionAndRiskById(
      assessmentName,
      id,
      remoteUtilityId
    ),
    () =>
      axAssetRegistry.getSystemConditionAndRiskById(
        assessmentName,
        id,
        remoteUtilityId
      ),
    {
      enabled: !!id && !!remoteUtilityId,
    }
  );
};
